import React from 'react';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { AssetUrls } from 'config/assets';
import { useGetAdMatchQuery } from 'store/services/play/playApi';
import { formatSearchValue } from 'constants/play';
import type { AdSlot } from 'utils/play/adSlot';
import './AdCard.css';

interface Props {
  slot: AdSlot;
  code: string;
  /** Stored format label, for the search deep link */
  formatLabel: string;
  /** Slot (b) eyebrow: "MATCH DONE · MM:SS LEFT IN ROUND" or "BYE ROUND" */
  eyebrow?: string;
}

const searchLink = (formatLabel: string): string => {
  const value = formatSearchValue(formatLabel);
  return value ? `/search?format=${encodeURIComponent(value)}` : '/search';
};

const Frame: React.FC<{ slot: AdSlot; eyebrow: string; children: React.ReactNode }> = ({ slot, eyebrow, children }) => (
  <aside className="play-ad" data-testid="play-ad" data-slot={slot}>
    <div className="play-ad__head">
      <img src={AssetUrls.logos.iconLogo} alt="" className="play-ad__logo" />
      <span className="play-ad__eyebrow">{eyebrow}</span>
      <span className="play-ad__label">Sponsored</span>
    </div>
    {children}
  </aside>
);

const Cta: React.FC<{ to: string; label: string }> = ({ to, label }) => (
  <Link to={to} className="play-ad__cta">
    {label}
    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
  </Link>
);

/**
 * Decision 25, player lobby review items 3 and 7: neutral sponsored card
 * (#121216, #2a2a2a33 border), an eyebrow row with the TradeMagic icon and
 * the Sponsored label, a 36px secondary CTA with the external-link icon.
 * Router links only (same origin, no reload). Slot (b) asks the ad-match
 * endpoint for a want-list card another player in the event has listed; with
 * no match it falls back to the slot (a) copy.
 */
export const AdCard: React.FC<Props> = ({ slot, code, formatLabel, eyebrow }) => {
  const { data: match } = useGetAdMatchQuery(code, { skip: slot !== 'b' });

  if (slot === 'd') {
    return (
      <Frame slot="d" eyebrow="TradeMagic Play">
        <p className="play-ad__title">Run your own event, free</p>
        <p className="play-ad__body">Pairings, the round clock, results and standings from your phone.</p>
        <Cta to="/play" label="Host an event" />
      </Frame>
    );
  }

  if (slot === 'c') {
    return (
      <Frame slot="c" eyebrow="GG">
        <p className="play-ad__title">Turn tonight's extras into cards you need</p>
        <p className="play-ad__body">List what you don't play and trade it for your next deck.</p>
        <Cta to="/my-cards/have-list" label="List cards" />
      </Frame>
    );
  }

  if (slot === 'b' && match) {
    return (
      <Frame slot="b" eyebrow={eyebrow ?? 'Match done'}>
        <div className="play-ad__match">
          {match.card.imageUrl && <img src={match.card.imageUrl} alt="" className="play-ad__card" loading="lazy" />}
          <div>
            <p className="play-ad__title">
              <span className="play-ad__card-name">{match.card.name}</span> is up for trade at this event
            </p>
            <p className="play-ad__body">{match.holderUsername} is playing here tonight and has it listed.</p>
          </div>
        </div>
        <Cta to={match.tradeUrl} label="View trade" />
      </Frame>
    );
  }

  // Slot (a), and slot (b) with no match
  return (
    <Frame slot={slot} eyebrow={slot === 'b' && eyebrow ? eyebrow : 'While you wait'}>
      <p className="play-ad__title">Missing a card for tonight?</p>
      <p className="play-ad__body">Search {formatLabel} cards other TradeMagic traders have listed.</p>
      <Cta to={searchLink(formatLabel)} label="Browse on TradeMagic" />
    </Frame>
  );
};

export default AdCard;
